/**
 * Provenance display adapter for Portfolio Insights.
 *
 * A persisted {@link ProvenanceEntry} carries only `capability_type`, `label`,
 * and a terminal `status`. The reused chat presentation (`ActivityChips`,
 * `ActivityTrace`, `orderActivities`) works on the {@link Activity} shape, which
 * additionally needs an `id` and a `ts`. This module synthesizes both:
 *
 *   - `id` is `prov-${index}`, stable for a given stored order.
 *   - `ts` is a synthetic, strictly increasing ISO-8601 UTC timestamp (one
 *     millisecond per entry from a fixed epoch). `orderActivities` sorts by `ts`,
 *     so this preserves the stored (ascending start time) order. The value is not
 *     a real event time and is never displayed.
 *
 * `capability_type` is passed through as `type`, so the three known capability
 * types render with their dedicated labels/icons and any future type renders
 * generically.
 *
 * Requirements: 12.5 (reuse the activity presentation), 12.8 (empty provenance).
 */
import type { Activity } from '@/types/chat';
import type { ProvenanceEntry } from '@/types/insights';

/** Fixed base for synthetic timestamps; only relative order matters. */
const SYNTHETIC_EPOCH_MS = Date.UTC(2000, 0, 1);

/** Map stored provenance entries onto the Activity shape, preserving order. */
export function provenanceToActivities(entries: ProvenanceEntry[]): Activity[] {
  return entries.map((entry, index) => ({
    id: `prov-${index}`,
    type: entry.capability_type,
    status: entry.status,
    label: entry.label,
    ts: new Date(SYNTHETIC_EPOCH_MS + index).toISOString(),
  }));
}

/** True when the report used no external capabilities (empty provenance). */
export function hasNoCapabilities(entries: ProvenanceEntry[]): boolean {
  return entries.length === 0;
}
